import { useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import MultiSelectDropdown from "@/app/components/ui/SelectMenus/MultiSelectDropdown/index";
import { FormRow, FULL_WIDTH_DROPDOWN, SubPanel } from "./components";
import { ISurgeRuleForm } from "./interface";

const TARGET_OPTIONS = [
  { id: "active", title: "All" },
  { id: "inactive", title: "Select Persona" },
];

// Same "User Persona" block as Coupon Setup > Applicability Conditions (no AND/OR: any tag matches).
const PersonaSection = ({
  formHook,
  personas,
  disabled,
}: {
  formHook: UseFormReturn<ISurgeRuleForm>;
  personas: string[];
  disabled: boolean;
}) => {
  const { watch, setValue, register, formState } = formHook;
  const included = watch("persona_included");

  const includeItems = useMemo(() => personas.map((title, index) => ({ id: index + 1, title })), [personas]);
  // Included personas can't also be excluded, as on the coupon page.
  const excludeItems = useMemo(
    () => personas.filter((p) => !included.includes(p)).map((title, index) => ({ id: index + 1, title })),
    [personas, included],
  );
  // MultiSelectDropdown re-fires onSelectItem when these change identity, so keep them stable.
  const onInclude = useCallback((selected: string[]) => setValue("persona_included", selected), [setValue]);
  const onExclude = useCallback((selected: string[]) => setValue("persona_excluded", selected), [setValue]);
  const defaultIncluded = useMemo(() => formHook.getValues("persona_included"), []); // eslint-disable-line react-hooks/exhaustive-deps
  const defaultExcluded = useMemo(() => formHook.getValues("persona_excluded"), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <FormRow label="Target Audience">
        <RadioGroupComponent
          alignment="horizontal"
          errorMessages={[]}
          fieldError={undefined}
          register={register("persona_mode")}
          groupOptions={TARGET_OPTIONS}
          defaultSelectedId={watch("persona_mode")}
          onRadioChange={(optionId: string) => {
            setValue("persona_included", []);
            setValue("persona_mode", optionId);
          }}
          radioOptionClassName="text-sm text-gray-700 font-medium"
          disabled={disabled}
        />
        {watch("persona_mode") === "inactive" && (
          <SubPanel>
            <MultiSelectDropdown
              data={includeItems}
              selectedKey="title"
              onSelectItem={onInclude}
              defaultSelected={defaultIncluded}
              required={true}
              register={register}
              name="persona_included"
              error={formState.errors.persona_included as never}
              containerClassName={FULL_WIDTH_DROPDOWN}
              disabled={disabled}
            />
          </SubPanel>
        )}
      </FormRow>
      <FormRow label="Exclude">
        <SimpleCheckbox
          onChange={() => {
            const next = !watch("exclude_enabled");
            setValue("exclude_enabled", next);
            if (!next) setValue("persona_excluded", []);
          }}
          checked={watch("exclude_enabled")}
          id="surge-exclude-users"
          colorClassName="text-[#202223] cursor-pointer"
          descriptionContent={
            <label htmlFor="surge-exclude-users" className="ml-3 block text-xs font-medium leading-6 text-gray-700">
              Exclude Users
            </label>
          }
          disabled={disabled}
        />
        {watch("exclude_enabled") && (
          <SubPanel>
            <MultiSelectDropdown
              data={excludeItems}
              selectedKey="title"
              onSelectItem={onExclude}
              defaultSelected={defaultExcluded}
              required={true}
              register={register}
              name="persona_excluded"
              error={formState.errors.persona_excluded as never}
              containerClassName={FULL_WIDTH_DROPDOWN}
              disabled={disabled}
            />
          </SubPanel>
        )}
      </FormRow>
    </>
  );
};

export default PersonaSection;
